import React, { useState } from 'react';
import { Forward, Hash, MessageCircle, Users, Search, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const CHANNELS = [
  { id: 'general', name: 'General', type: 'channel' },
  { id: 'operations', name: 'Operations', type: 'channel' },
  { id: 'customer-relations', name: 'Customer Relations', type: 'channel' },
  { id: 'managers', name: 'Management', type: 'channel' },
  { id: 'announcements', name: 'Announcements', type: 'channel' },
];

const ChatForwardModal = ({ open, onClose, message, staffList, groups, onForward }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const filteredChannels = CHANNELS.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
  const filteredStaff = (staffList || []).filter(s =>
    s.username.toLowerCase().includes(search.toLowerCase()) ||
    (s.display_name && s.display_name.toLowerCase().includes(search.toLowerCase()))
  );
  const filteredGroups = (groups || []).filter(g => g.name?.toLowerCase().includes(search.toLowerCase()));

  const handleForward = () => {
    if (!selected) { toast.error('Select a destination'); return; }
    onForward(message.id, selected, selectedType);
    setSelected(null);
    setSelectedType(null);
    setSearch('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Forward className="w-4 h-4 text-cyan-400" />
            Forward Message
          </DialogTitle>
        </DialogHeader>
        {message && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 mb-3">
            <p className="text-xs text-indigo-400 mb-1">{message.sender_display_name || message.sender_username}</p>
            <p className="text-sm text-slate-300 line-clamp-2">{message.message}</p>
          </div>
        )}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search channels, people, groups..."
            className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50" 
            data-testid="forward-search" />
        </div>
        <div className="max-h-60 overflow-y-auto space-y-1" data-testid="forward-targets">
          {filteredChannels.length > 0 && (
            <>
              <p className="text-slate-500 text-[10px] uppercase tracking-wider px-2 pt-1">Channels</p>
              {filteredChannels.map(ch => (
                <button key={ch.id} onClick={() => { setSelected(ch.id); setSelectedType('channel'); }}
                  className={`w-full flex items-center gap-2.5 p-2 rounded-lg transition-all ${
                    selected === ch.id && selectedType === 'channel' ? 'bg-cyan-500/15 border border-cyan-500/30' : 'hover:bg-slate-800/50'
                  }`}>
                  <Hash className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">{ch.name}</span>
                  {selected === ch.id && selectedType === 'channel' && <Check className="w-4 h-4 text-cyan-400 ml-auto" />}
                </button>
              ))}
            </>
          )}
          {filteredStaff.length > 0 && (
            <>
              <p className="text-slate-500 text-[10px] uppercase tracking-wider px-2 pt-2">Direct Messages</p>
              {filteredStaff.slice(0, 8).map(s => (
                <button key={s.username} onClick={() => { setSelected(s.username); setSelectedType('dm'); }}
                  className={`w-full flex items-center gap-2.5 p-2 rounded-lg transition-all ${
                    selected === s.username && selectedType === 'dm' ? 'bg-cyan-500/15 border border-cyan-500/30' : 'hover:bg-slate-800/50'
                  }`}>
                  <MessageCircle className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">{s.display_name || s.username}</span>
                  {selected === s.username && selectedType === 'dm' && <Check className="w-4 h-4 text-cyan-400 ml-auto" />}
                </button>
              ))}
            </>
          )}
          {filteredGroups.length > 0 && (
            <>
              <p className="text-slate-500 text-[10px] uppercase tracking-wider px-2 pt-2">Groups</p>
              {filteredGroups.map(g => (
                <button key={g.id} onClick={() => { setSelected(g.id); setSelectedType('group'); }}
                  className={`w-full flex items-center gap-2.5 p-2 rounded-lg transition-all ${
                    selected === g.id && selectedType === 'group' ? 'bg-cyan-500/15 border border-cyan-500/30' : 'hover:bg-slate-800/50'
                  }`}>
                  <Users className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">{g.name}</span>
                  {selected === g.id && selectedType === 'group' && <Check className="w-4 h-4 text-cyan-400 ml-auto" />}
                </button>
              ))}
            </>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-slate-700 text-slate-300">Cancel</Button>
          <Button onClick={handleForward} disabled={!selected}
            className="flex-1 bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-600 hover:to-indigo-700 text-white disabled:opacity-40">
            Forward
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatForwardModal;
